import type {
  InstalledLabwareItem,
  ModulePosition,
  TemplateList,
  WellPointOffsets,
} from '@ctcells/api'

/**
 * 자리의 설치 정의 고치기 — 본문 짓기 · 점검 줄 읽기(`RCP-LBW-09` · `IFC-RO-127` · 모듈 `IFC-RM-35`).
 *
 * 🔴 **서버 쓰기는 통째로 바꾼다**(모듈 `set_installed_labware` 「설치 정의 목록을 통째로 바꾼다(빈 목록 = 걷음)」 ·
 *    코어 `installed_labware.entries_of`). 목록에 안 실은 항목은 걷힌다 — 그래서 **받은 목록 위에 고친 것만 얹는다**(CLAUDE.md PUT 절).
 * - 이미 있는 항목은 `{definitionUri, well_point_offsets}`(부분 갱신)로 다시 싣는다 — 모듈이 저장된 정의 원문을 **그대로** 둔다
 *   (`_with_kept_definitions` — 원문의 모든 칸 · 화면이 모르는 칸까지). 정의 원문을 화면이 다시 짓지 않는다
 * - 분주점 오프셋은 그 항목 맵 **전체**를 싣는다(맵이 통째로 바뀐다) — 받은 맵 위에 고친 웰의 x · y 만 얹는다.
 *   화면에 안 그린 웰 · 고치지 않은 웰은 받은 값 그대로다. 받은 맵이 없으면(`null` · 칸 없음 = 모든 웰 0) `null` 로 둔다
 * - 새 항목은 랩웨어 템플릿 id 글자 하나(코어가 템플릿 정의 원문을 싣는다 · 오프셋 없음). 사람이 오프셋 칸을 채우면
 *   `{labware_id, well_point_offsets}` — 웰은 **그 템플릿 정의의 `wells` 이름 그대로**(지어내지 않는다). 🔴 칸은 **비어서** 열린다 —
 *   0 을 미리 채우지 않는다(사람이 0 을 적어야 «있다»가 된다는 서버 규칙이 화면에서 보여야 한다 · 팀장 2026-10-23). 하나라도 채우면 전부 채워야 한다
 * - 🔴 원점이 미티칭인 자리는 오프셋이 있어야 코어 사본이 그 정의를 «있다»고 본다(`position_copy.installed_uris` — 분주점 또는
 *   분주점 오프셋의 키). 오프셋 없이 넣으면 `RCP-LBW-09` 가 «사본 없음 — 판정 불능»으로 남는다(실측 2026-10-23)
 */

/** 오픈트론 labware/2 정의의 이름 `<namespace>/<loadName>/<version>` — 코어 `deck_geometry.definition_uri` 와 같은 규칙. 칸이 모자라면 없음 */
export function definitionUriOf(definition: unknown): string | undefined {
  if (typeof definition !== 'object' || definition === null) return undefined
  const { namespace, version, parameters } = definition as Record<string, unknown>
  const loadName =
    typeof parameters === 'object' && parameters !== null
      ? (parameters as Record<string, unknown>)['loadName']
      : undefined
  if (typeof namespace !== 'string' || typeof loadName !== 'string') return undefined
  if (typeof version !== 'number' || !Number.isInteger(version)) return undefined
  return `${namespace}/${loadName}/${String(version)}`
}

/** 정의 원문의 사람 이름(`metadata.displayName`) — 없으면 없음 */
export function definitionDisplayName(definition: unknown): string | undefined {
  if (typeof definition !== 'object' || definition === null) return undefined
  const metadata = (definition as Record<string, unknown>)['metadata']
  if (typeof metadata !== 'object' || metadata === null) return undefined
  const name = (metadata as Record<string, unknown>)['displayName']
  return typeof name === 'string' && name !== '' ? name : undefined
}

/** 점검 줄이 가리키는 자리 · 찾는 정의 */
export interface Lbw09Target {
  positionId: string
  /** 레시피 폐기통 정의 이름 — 줄에 없으면 없음 */
  definitionUri?: string
}

const URI = /^[^\s/]+\/[^\s/]+\/\d+$/

/**
 * `RCP-LBW-09` 줄 읽기 — 서버 원문(`preflight.trash_install_blockers`) 두 꼴:
 * `RCP-LBW-09 <자리>: 설치 정의 목록에 없음 <uri>` · `RCP-LBW-09 <자리>: 설치 정의 사본 없음 — <uri> 를 확인할 수 없다`.
 * 🔴 자리 이름을 못 읽으면 없음이다 — 자리를 지어내지 않는다.
 */
export function lbw09Target(item: string | null): Lbw09Target | undefined {
  if (item === null) return undefined
  const words = item.split(/\s+/)
  if (words[0] !== 'RCP-LBW-09') return undefined
  const point = words[1]
  if (point?.endsWith(':') !== true || point.length < 2) return undefined
  const uri = words.slice(2).find((word) => URI.test(word))
  return { positionId: point.slice(0, -1), ...(uri === undefined ? {} : { definitionUri: uri }) }
}

/** 받은 설치 정의 항목 하나(모듈 `Position.installed_labware[]`) */
export type InstalledEntry = NonNullable<ModulePosition['installed_labware']>[number]

/** 고친 웰 한 칸 — 입력 글자 그대로(수로 바꾸는 것은 본문을 지을 때) */
export interface WellEdit {
  x?: string
  y?: string
}

export interface KeptRow {
  definitionUri: string
  /** 받은 오프셋 맵 — `null` = 받은 값이 없음(모든 웰 0) */
  offsets: WellPointOffsets
  /** 받은 정의 원문의 웰 이름 — 받은 오프셋이 없을 때 빈 칸을 여는 출처(새 항목과 같은 규칙) */
  wells: readonly string[]
  removed: boolean
  edits: Readonly<Record<string, WellEdit>>
}

export interface AddedRow {
  labwareId: string
  definitionUri: string
  /** 템플릿 정의의 웰 이름(`definition.wells` 키) — 오프셋 칸의 출처 */
  wells: readonly string[]
  /** 웰마다 x · y 입력 — 비면 오프셋 없이 넣는다 */
  edits: Readonly<Record<string, WellEdit>>
}

/** 템플릿 정의의 웰 이름 — 정의 원문 `wells` 의 키 그대로 */
export function definitionWells(definition: unknown): string[] {
  if (typeof definition !== 'object' || definition === null) return []
  const wells = (definition as Record<string, unknown>)['wells']
  return typeof wells === 'object' && wells !== null ? Object.keys(wells) : []
}

export interface InstalledDraft {
  kept: readonly KeptRow[]
  added: readonly AddedRow[]
}

export function installedDraftOf(entries: readonly InstalledEntry[] | undefined): InstalledDraft {
  return {
    kept: (entries ?? []).map((entry) => ({
      definitionUri: entry.definitionUri,
      offsets: entry.well_point_offsets ?? null,
      wells: definitionWells(entry),
      removed: false,
      edits: {},
    })),
    added: [],
  }
}

/** 입력 글자 → 수. 빈 칸 · 수가 아닌 글자는 없음 */
function numberOf(text: string | undefined): number | undefined {
  if (text === undefined) return undefined
  const trimmed = text.trim()
  if (trimmed === '') return undefined
  const value = Number(trimmed)
  return Number.isFinite(value) ? value : undefined
}

/** 고친 칸 가운데 수가 아닌 것 — 있으면 `웰.축` 목록 */
export function invalidEdits(draft: InstalledDraft): string[] {
  const rows = [...draft.kept.filter((row) => !row.removed), ...draft.added]
  const out: string[] = []
  for (const row of rows) {
    for (const [well, edit] of Object.entries(row.edits)) {
      for (const axis of ['x', 'y'] as const) {
        const text = edit[axis]
        if (text !== undefined && numberOf(text) === undefined) out.push(`${well}.${axis}`)
      }
    }
  }
  return out
}

/** 빈 칸으로 연 웰 칸의 채움 — 하나도 안 채웠나(`none`) · 전부 채웠나(`all`) · 일부만(`partial` · 보내지 않는다) */
export function blankFill(row: {
  wells: readonly string[]
  edits: Readonly<Record<string, WellEdit>>
}): 'none' | 'all' | 'partial' {
  const cells = row.wells.flatMap((well) => [row.edits[well]?.x, row.edits[well]?.y])
  const filled = cells.filter((text) => text !== undefined && text.trim() !== '').length
  return filled === 0 ? 'none' : filled === cells.length ? 'all' : 'partial'
}

/** 빈 칸으로 연 줄 — 새 항목 전부 · 받은 오프셋이 없는 항목 */
function blankRows(draft: InstalledDraft) {
  return [...draft.kept.filter((row) => !row.removed && row.offsets === null), ...draft.added]
}

/** 일부만 채운 줄의 `definitionUri` — 있으면 보내지 않는다 */
export function partialRows(draft: InstalledDraft): string[] {
  return blankRows(draft)
    .filter((row) => blankFill(row) === 'partial')
    .map((row) => row.definitionUri)
}

/** 빈 칸 줄의 오프셋 — 전부 채웠을 때만. 웰마다 {x, y, z: 0}(z 는 서버가 0 만 받는다 · 높이는 이 칸이 옮기지 않는다) */
export function filledOffsets(row: {
  wells: readonly string[]
  edits: Readonly<Record<string, WellEdit>>
}): NonNullable<WellPointOffsets> | undefined {
  if (row.wells.length === 0 || blankFill(row) !== 'all') return undefined
  const out: NonNullable<WellPointOffsets> = {}
  for (const well of row.wells) {
    const x = numberOf(row.edits[well]?.x)
    const y = numberOf(row.edits[well]?.y)
    if (x === undefined || y === undefined) return undefined
    out[well] = { x, y, z: 0 }
  }
  return out
}

/**
 * 받은 맵 위에 고친 x · y 만 얹는다 — 맵에 없는 웰은 만들지 않는다(받은 웰만 그렸다) · z 는 받은 값 그대로.
 * 받은 맵이 없으면 정의 웰의 빈 칸을 전부 채웠을 때만 그 값, 아니면 `null`(받은 그대로).
 */
export function mergedOffsets(row: KeptRow): WellPointOffsets {
  if (row.offsets === null) return filledOffsets(row) ?? null
  const out: NonNullable<WellPointOffsets> = {}
  for (const [well, vector] of Object.entries(row.offsets)) {
    const edit = row.edits[well]
    out[well] = {
      ...vector,
      x: numberOf(edit?.x) ?? vector.x,
      y: numberOf(edit?.y) ?? vector.y,
    }
  }
  return out
}

/** 본문 `labware_ids` — 남긴 항목(부분 갱신 꼴) + 새 항목(템플릿 id) */
export function installedLabwareItems(draft: InstalledDraft): InstalledLabwareItem[] {
  return [
    ...draft.kept
      .filter((row) => !row.removed)
      .map((row) => ({ definitionUri: row.definitionUri, well_point_offsets: mergedOffsets(row) })),
    ...draft.added.map((row) => {
      const offsets = filledOffsets(row)
      return offsets === undefined
        ? row.labwareId
        : { labware_id: row.labwareId, well_point_offsets: offsets }
    }),
  ]
}

/** 바뀐 것이 있나 — 걷음 · 새 항목 · 값이 달라진 오프셋 */
export function installedDraftChanged(draft: InstalledDraft): boolean {
  if (draft.added.length > 0) return true
  return draft.kept.some((row) => {
    if (row.removed) return true
    if (row.offsets === null) return filledOffsets(row) !== undefined
    const merged = mergedOffsets(row)
    return Object.entries(row.offsets).some(([well, vector]) => {
      const next = merged?.[well]
      return next !== undefined && (next.x !== vector.x || next.y !== vector.y)
    })
  })
}

/** 지금 목록(남긴 것 + 새 것)에 그 정의가 있나 — 같은 `definitionUri` 둘이면 서버 422 */
export function draftHasUri(draft: InstalledDraft, uri: string): boolean {
  return (
    draft.kept.some((row) => !row.removed && row.definitionUri === uri) ||
    draft.added.some((row) => row.definitionUri === uri)
  )
}

export interface TemplateOption {
  labwareId: string
  definitionUri: string
  label: string
  wells: string[]
}

/** 템플릿 목록 → 고를 것. 정의에서 이름을 못 지으면 뺀다(서버도 그 템플릿은 422 다 — `definitions_of`) */
export function templateOptions(list: TemplateList | undefined): TemplateOption[] {
  if (list === undefined) return []
  return list.items.flatMap((row) => {
    const id = row[list.key]
    const definition = row['definition']
    const uri = definitionUriOf(definition)
    if (typeof id !== 'string' || uri === undefined) return []
    const name = row['display_name']
    return [
      {
        labwareId: id,
        definitionUri: uri,
        label: typeof name === 'string' ? name : id,
        wells: definitionWells(definition),
      },
    ]
  })
}
